import classNames from "classnames";
import { TableUI } from "components/design/TableUI";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { MinusCircle } from "components/icons/MinusCircle";
import {
  Field,
  FieldProps,
  Form,
  Formik,
  getIn,
  useFormikContext,
} from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GrayPlusCircle from "static/images/GrayPlusCircle.svg";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { PriceInput } from "components/PriceInput/PriceInput";
import { GrayDeleteIcon } from "components/icons/GrayDeleteIcon";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import {
  useGetAllInvoiceCustomersQuery,
  useGetEntityInvoiceQuery,
  useUpdateInvoiceMutation,
} from "store/apis/invoices";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { InvoiceCustomerModal } from "components/InvoiceCustomerModal/InvoiceCustomerModal";
import { SingleValue } from "react-select";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { useGetAllTaxCodeQuery } from "store/apis/taxCalculator";
import { updateTransactionValidation } from "formValidations/updateTransactionValidation";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { TaxCode } from "types/Models/taxCalculate";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { SelectOrEditCustomer } from "./SelectOrEditCustomer";
import { SALES_TAX_INVOICE } from "constants/invoiceType";
import { BULK_SALES_TAX_INVOICE_EXCEL_TEMPLATE_URL } from "types/contants/sampleExcelSheet";
import { debounce } from "utils/debouncing";
import { Invoice, InvoiceItem } from "types/Models/invoice";

const createColumn = createColumnHelper<InvoiceItem>();

const CustomerComboBox = () => {
  const entityId = useCurrentEntityId();
  const group = useCurrentGroupContext();
  const [customerSearchTerm, setCustomerSearchTerm] = useState<string>();

  const { data: allCustomersData, isLoading } = useGetAllInvoiceCustomersQuery(
    {
      groupId: group?.uuid!,
      entityId: entityId!,
      searchTerm: customerSearchTerm,
    },
    { skip: !group?.uuid || !entityId }
  );
  const allCustomers = allCustomersData?.recipients;

  const customerOptions = allCustomers?.map((v) => ({
    label: v.company_name,
    value: v.customer_id,
  }));

  const [customerId, setCustomerId] = useState<string | null | "NEW">("");

  const { values, setFieldValue } = useFormikContext<Invoice>();

  const { data: selectedCustomerData } = useGetAllInvoiceCustomersQuery(
    {
      groupId: group?.uuid!,
      entityId: entityId!,
      customerId: values?.customer_id,
    },
    { skip: !group?.uuid || !entityId || !values?.customer_id }
  );
  const selectedCustomer = selectedCustomerData?.recipients?.[0];

  const onCustomerSearch = useMemo(
    () =>
      debounce((value: string) => setCustomerSearchTerm(value || undefined)),
    []
  );

  const onCustomerSelect = (data: SingleValue<OptionData>) => {
    const newlySelectedCustomer = allCustomers?.find(
      ({ customer_id }) => customer_id === data?.value
    );
    setFieldValue("customer_id", data?.value);
    setFieldValue("customer", newlySelectedCustomer);
  };

  return (
    <>
      <Combobox
        withForm
        label="Customer"
        size="small"
        isLoading={isLoading}
        options={customerOptions}
        onInputChange={onCustomerSearch}
        filterOption={null}
        menuPortalTarget={document.body}
        onChange={(v) => onCustomerSelect(v as SingleValue<OptionData>)}
        name="customer_id"
        value={
          values?.customer_id
            ? {
                value: values?.customer_id,
                label: selectedCustomer?.company_name,
              }
            : null
        }
        actions={
          <Button
            type="button"
            onClick={() => setCustomerId("NEW")}
            customType="link"
          >
            Add customer
          </Button>
        }
        components={{
          Option: SelectOrEditCustomer,
          NoOptionsMessage: () => (
            <p className="t-m-0 t-text-center t-py-4">No customers added</p>
          ),
        }}
        required
        //@ts-ignore
        onEditCustomer={setCustomerId}
      />
      {customerId && (
        <InvoiceCustomerModal
          customerId={customerId}
          setCustomerId={setCustomerId}
        />
      )}
    </>
  );
};

const ItemDetailsTable = ({ productCode }: { productCode?: TaxCode[] }) => {
  const { values, setFieldValue } = useFormikContext<Invoice>();
  const { info: infoToast } = useToast();

  const deleteItemRow = useCallback(
    (id: number) => {
      const newValues = [...values.invoice_items];
      if (newValues.length === 1) {
        infoToast({
          title: "Invoice Item Required",
          message: "One line item is required to proceed",
        });
        return;
      }
      newValues.splice(id, 1);
      setFieldValue("invoice_items", newValues);
    },
    [values.invoice_items.length]
  );

  const productCodeOptions = productCode?.map((v) => ({
    label: v.tax_code,
    value: v.uuid,
  }));

  const columns = useMemo(
    () => [
      createColumn.display({
        header: "S. NO.",
        id: "serial_number",
        size: 5,
        cell: (info) => {
          return (
            <div className="t-flex t-justify-center">
              <span className="t-text-text-30 t-text-body-sm">
                {info.row.index + 1}.
              </span>
            </div>
          );
        },
      }),
      createColumn.accessor("description", {
        header: () => (
          <p className="t-m-0">
            ITEM NAME <span className="t-text-subtext-sm t-text-red">*</span>
          </p>
        ),
        size: 20,
        cell: (info) => {
          return (
            <Field>
              {({ form: { values } }: FieldProps) => {
                const name = `invoice_items[${info.row.id}].description`;
                const value = getIn(values, name);

                return (
                  <TextInput
                    customSize="small"
                    defaultValue={value}
                    name={name}
                  />
                );
              }}
            </Field>
          );
        },
      }),
      createColumn.accessor("product_code", {
        header: () => (
          <p className="t-m-0">
            PRODUCT CODE <span className="t-text-subtext-sm t-text-red">*</span>
          </p>
        ),
        size: 20,

        cell: (info) => {
          return (
            <Field>
              {({ form: { values } }: FieldProps) => {
                const name = `invoice_items[${info.row.id}].product_code`;
                const value = getIn(values, name);
                const product_code = productCode?.find(
                  ({ uuid }) => uuid === value
                );
                return (
                  <Combobox
                    withForm
                    label=""
                    value={
                      value
                        ? {
                            label: product_code?.tax_code,
                            value,
                          }
                        : null
                    }
                    name={name}
                    size="small"
                    options={productCodeOptions}
                    menuPortalTarget={document.body}
                    className="t-w-full "
                    required
                  />
                );
              }}
            </Field>
          );
        },
      }),
      createColumn.accessor("quantity", {
        header: () => <p className="t-m-0 t-text-center">QTY</p>,
        size: 15,
        cell: (info) => {
          return (
            <Field>
              {({ form: { values } }: FieldProps) => {
                const name = `invoice_items[${info.row.id}].quantity`;
                const quantity = getIn(values, name);

                return (
                  <div className="t-flex t-justify-center t-items-center t-gap-1.5">
                    <ConditionalToolTip
                      text="Quantity can’t be zero or negative"
                      condition={quantity <= 1}
                      side="bottom"
                    >
                      <div>
                        <Button
                          disabled={quantity <= 1}
                          type="button"
                          customType="ghost"
                          onClick={() => setFieldValue(name, quantity - 1)}
                        >
                          <div
                            className={classNames({
                              "t-text-text-30": quantity > 1,
                              "t-text-neutral-20": quantity <= 1,
                            })}
                          >
                            <MinusCircle color="currentColor" />
                          </div>
                        </Button>
                      </div>
                    </ConditionalToolTip>
                    <p className="t-text-body-sm t-text-text-30 t-m-0 t-border-solid t-border t-border-neutral-10 t-overflow-x-auto t-w-12 t-text-center t-py-1 t-rounded-md">
                      {quantity}
                    </p>
                    <Button
                      type="button"
                      customType="ghost"
                      onClick={() => setFieldValue(name, quantity + 1)}
                    >
                      <img src={GrayPlusCircle} alt="GrayPlusCircle" />
                    </Button>
                  </div>
                );
              }}
            </Field>
          );
        },
      }),
      createColumn.accessor("rate", {
        header: () => (
          <p className="t-m-0">
            ITEM RATE <span className="t-text-subtext-sm t-text-red">*</span>
          </p>
        ),
        size: 20,
        cell: (info) => {
          const name = `invoice_items[${info.row.id}].rate`;
          return (
            <Field>
              {({ form: { values } }: FieldProps) => {
                const value = getIn(values, name);

                return (
                  <PriceInput
                    rightAlign
                    defaultValue={value}
                    customSize="small"
                    name={name}
                  />
                );
              }}
            </Field>
          );
        },
      }),
      createColumn.accessor("amount", {
        header: () => (
          <p className="t-m-0 t-text-end">
            AMOUNT <span className="t-text-subtext-sm t-text-red">*</span>
          </p>
        ),
        size: 15,
        cell: (info) => {
          return (
            <div className="t-flex t-justify-end">
              <Field>
                {({ form: { values } }: FieldProps) => {
                  const rate = getIn(
                    values,
                    `invoice_items[${info.row.id}].rate`
                  );
                  const quantity = getIn(
                    values,
                    `invoice_items[${info.row.id}].quantity`
                  );

                  return (
                    <p className="t-text-text-60 t-text-body-sm t-m-0">
                      <AmountSuperScript amount={rate * quantity} />
                    </p>
                  );
                }}
              </Field>
            </div>
          );
        },
      }),
      createColumn.display({
        id: "deleteId",
        header: "",
        size: 10,
        cell: (info) => {
          return (
            <Button
              type="button"
              onClick={() => {
                deleteItemRow(Number(info.row.id));
              }}
              customType="ghost"
            >
              <div className="t-text-text-30 t-ml-4">
                <GrayDeleteIcon />
              </div>
            </Button>
          );
        },
      }),
    ],
    [values.invoice_items.length]
  );
  const table = useReactTable({
    data: values.invoice_items,
    getCoreRowModel: getCoreRowModel(),
    columns: columns,
    defaultColumn: {
      minSize: 6,
    },
  });
  return <TableUI table={table} />;
};

export const EditTransactions = ({
  isOpen,
  selectedTransactionId,
  onClose,
  selectedInvoiceId,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedTransactionId: string | null;
  selectedInvoiceId: string | null;
}) => {
  const group = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const {
    data: transactionDetails,
    isLoading,
    isSuccess,
    isFetching,
  } = useGetEntityInvoiceQuery(
    {
      groupId: group?.uuid!,
      entityId,
      invoiceId: selectedInvoiceId!,
      type: SALES_TAX_INVOICE,
    },
    { skip: !group?.uuid || !entityId || !selectedInvoiceId }
  );

  const totalAmountRef = useRef<HTMLDivElement>(null);
  const [newItemAdded, setNewItemAdded] = useState<number>(1);

  const { data: productCode, isLoading: productCodeLoading } =
    useGetAllTaxCodeQuery({ entityId }, { skip: !entityId });

  const { alertToast, successToast } = useToast();

  const [updateInvoice, { isLoading: isUpdatingInvoice }] =
    useUpdateInvoiceMutation();

  const handleUpdateInvoice = async (values: Invoice) => {
    const { financial_record_type, ...rest } = values;
    const payload = {
      ...rest,
      txn_id: selectedTransactionId,
      type: SALES_TAX_INVOICE,
    };

    try {
      await updateInvoice({
        groupId: group.uuid,
        entityId,
        invoiceId: selectedInvoiceId!,
        payload: payload,
        type: SALES_TAX_INVOICE,
      }).unwrap();
      successToast({
        message: "Transaction edited successfully",
      });
      onClose();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as {}
      );
    }
  };

  const getTotalAmount = (items?: InvoiceItem[]): number => {
    if (!items) return 0;
    return items.reduce((total, item) => {
      const itemAmount = item.quantity * item.rate;
      return total + itemAmount;
    }, 0);
  };

  const initialValues = {
    ...transactionDetails,
  };

  useEffect(() => {
    if (totalAmountRef.current) {
      totalAmountRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  }, [newItemAdded]);

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="xxl">
        <Async.Root
          isLoading={isLoading || isFetching}
          isSuccess={Boolean(transactionDetails)}
          isEmpty={isSuccess && !transactionDetails}
        >
          <Async.Success>
            <Formik
              initialValues={initialValues}
              validationSchema={updateTransactionValidation}
              onSubmit={(values) => {
                handleUpdateInvoice(values as Invoice);
              }}
              validateOnChange={false}
            >
              {({ setFieldValue, values }) => {
                const onAddNewItem = () => {
                  const newItem = {
                    description: "",
                    product_code: "",
                    quantity: 1,
                    rate: 0,
                    amount: 0,
                  };
                  setFieldValue("invoice_items", [
                    ...(values.invoice_items || []),
                    newItem,
                  ]);
                  setNewItemAdded(values?.invoice_items?.length || 0);
                };
                return (
                  <Form>
                    <Modal.Header>
                      <Modal.Title>Edit transaction</Modal.Title>
                      <Modal.Close />
                    </Modal.Header>
                    <Modal.Body className="t-max-h-[50vh] t-overflow-y-scroll">
                      <div className="t-flex t-flex-row t-gap-x-5">
                        <div className="t-w-[25%] t-flex t-flex-col t-gap-y-4">
                          <p className="t-text-subtitle t-text-text-100 t-m-0">
                            Customer details
                          </p>
                          <div>
                            <CustomerComboBox />
                            {!values.customer?.billing_address?.state &&
                              values.customer && (
                                <p className="t-text-red t-text-body-sm t-m-0 t-mt-1.5">
                                  Billing address is required
                                </p>
                              )}
                          </div>
                          <div>
                            <span className="t-text-body-sm t-text-text-30 t-m-0">
                              State
                            </span>
                            <p className="t-text-text-60 t-text-body-sm t-m-0">
                              {values?.customer?.billing_address?.state
                                ? values?.customer?.billing_address?.state
                                : "-"}
                            </p>
                          </div>
                        </div>
                        <span className="t-bg-neutral-10 t-w-[1px] t-h-32 t-self-center" />

                        <div className="t-flex-grow t-flex t-flex-col t-gap-y-4 ">
                          <p className="t-text-subtitle t-text-text-100 t-m-0">
                            Transaction details
                          </p>
                          <div className="t-grid t-grid-cols-3 t-gap-4">
                            <PriceInput
                              label="Tax collected"
                              customSize="small"
                              name="tax"
                              required
                            />
                            <PriceInput
                              label="Amount paid"
                              customSize="small"
                              name="paid_amount"
                            />
                            <PriceInput
                              label="Shipping/Handling charges"
                              customSize="small"
                              name="shipping_or_handling_charges"
                            />

                            <Field name="invoice_date">
                              {({ field }: FieldProps) => (
                                <DateInput
                                  {...field}
                                  label="Invoice date"
                                  required
                                  customSize="small"
                                  portalId={field.name}
                                />
                              )}
                            </Field>
                          </div>
                        </div>
                      </div>
                      <div className="t-mt-4">
                        <p className="t-text-subtitle t-text-text-100 t-m-0">
                          Item details
                        </p>
                        <span className="t-text-text-30 t-text-body-sm">
                          Transaction line items and their details
                        </span>
                        <div className="t-mt-3">
                          <ItemDetailsTable productCode={productCode} />
                          <div
                            className="t-flex t-justify-between t-mt-4 t-mr-16"
                            ref={totalAmountRef}
                          >
                            <Button
                              size="small"
                              type="button"
                              onClick={onAddNewItem}
                            >
                              Add item
                            </Button>
                            <div className="t-flex t-w-64 -t-mr-1 t-flex-row t-justify-between t-items-center ">
                              <p className="t-text-text-100 t-m-0 t-text-subtext">
                                Total
                              </p>
                              <span className="t-text-text-100 t-m-0 t-text-subtext">
                                <AmountSuperScript
                                  amount={getTotalAmount(values?.invoice_items)}
                                />
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Modal.Body>
                    <Modal.FooterButtonGroup>
                      <Button size="small" onClick={onClose}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        isLoading={isUpdatingInvoice}
                        customType="primary"
                      >
                        Save
                      </Button>
                    </Modal.FooterButtonGroup>
                  </Form>
                );
              }}
            </Formik>
          </Async.Success>
        </Async.Root>
      </Modal.Content>
    </Modal.Root>
  );
};
